

import {
    Component,
    Prop,
    Vue,
} from 'vue-property-decorator';
import {
    ProjectGroup,
    DailyWageReport,
    DailyWageReportLine,
    Repair,
    User,
} from '@/clients/omb-api';
import Loading from '@/components/Loading.vue';
import InputButton from '@/components/input/InputButton.vue';
import clone from 'clone';
import InputDefault from '@/components/input/InputDefault.vue';
import moment from 'moment';
import Api from '@/assets/ts/Api';
import DailyWageReportTableLine from '@/components/reports/DailyWageReportTableLine.vue';
import InputTextarea from '@/components/input/InputTextarea.vue';
import ExportReportDialog from '@/components/dialogs/ExportReportDialog.vue';
import Uploader from '@/assets/ts/uploader';
import { ROLE } from '@/assets/ts/enum/role';
import AlertErrorDetails from '@/components/AlertErrorDetails.vue';
import ErrorUtils from '@/assets/ts/error_utils';
import Service from '@/services/Service';
import ConnectionUtils from '@/assets/ts/connection_utils';
import { SaveReportResult } from '@/assets/ts/types/SaveReportResult';
import BaseDialog from './BaseDialog.vue';

@Component({
    computed: {
        ROLE() {
            return ROLE;
        },
    },
    components: {
        AlertErrorDetails,
        ExportReportDialog,
        InputTextarea,
        DailyWageReportTableLine,
        InputDefault,
        InputButton,
        Loading,
        BaseDialog,
    },
})
export default class EditDailyWageReportDialog extends Vue {

    @Prop({ default: null })
    private existing!: DailyWageReport | null;

    @Prop({ required: true })
    private parent!: ProjectGroup | Repair;

    @Prop({ required: true })
    private parentType!: 'project-group' | 'repair';

    @Prop({ required: true })
    private currentUser!: User;

    @Prop({ default: false })
    private readonly!: boolean;

    private ready = false;
    private report: DailyWageReport | null = null;
    private totalHours = 0;

    private showErrors: boolean = false;
    private errors: { [key: string]: string } = {};
    private generalError: any | null = null;
    private localSaveError = false;
    private remoteSaveError = false;
    private deleting: boolean = false;
    private submitting: boolean = false;

    private title = 'Tagelohnzettel';

    private lineToRemove?: number | null = null;
    private exportDialogVisible = false;
    private deleteDialogVisible = false;

    private autosaveTimeout?: any;
    private lastSave: Date | null = null;

    private uploader?: Uploader;

    private isOnline = false;

    private get isSigned(): boolean {
        return !!this.report?.fileId || !!this.report?.signature;
    }

    private formatDate(date: Date): string {
        const hours = date.getHours() < 10 ? `0${date.getHours()}` : `${date.getHours()}`;
        const minutes = date.getMinutes() < 10 ? `0${date.getMinutes()}` : `${date.getMinutes()}`;

        return `${hours}:${minutes}`;
    }

    private async created() {
        this.isOnline = ConnectionUtils.addConnectionChangeListener((status) => this.isOnline = status);

        try {
            this.uploader = await Uploader.create();
        } catch (e) {
            console.error('failed to create uploader', e);
        }

        if (this.existing) {
            if (this.existing.id) {
                Api.omb.getDailyWageReport(this.existing.id)
                    .then((r) => {
                        this.setReport(r);
                    })
                    .catch((e) => {
                        console.error('failed to fetch remote report', e);

                        Service.reports.getDailyWageReport(this.existing?.id ?? 0)
                            .then((r) => this.setReport(r))
                            .catch((e2) => {
                                console.error('failed to fetch local report', e2);
                            });
                    });
            } else {
                this.setReport(this.existing);
            }
        } else {
            await this.setInitialValues();
            this.setTitle();
            this.ready = true;
        }
    }

    private setReport(report: DailyWageReport): void {
        this.report = clone(report);
        this.correctDateTypes();
        this.setTitle();
        this.ready = true;
    }

    private setTitle(): void {
        if (this.isSigned || this.readonly) {
            this.title = 'Tagelohnzettel';
        } else if (this.report?.id) {
            this.title = 'Tagelohnzettel bearbeiten';
        } else {
            this.title = 'Neuen Tagelohnzettel erstellen';
        }
    }

    private setTotalHours(): void {
        if (!this.report) return;

        let sum = 0;
        if (this.report.lines) {
            this.report.lines.forEach((line) => {
                const mo = parseFloat(`${line.monday || 0}`);
                const tu = parseFloat(`${line.tuesday || 0}`);
                const we = parseFloat(`${line.wednesday || 0}`);
                const th = parseFloat(`${line.thursday || 0}`);
                const fr = parseFloat(`${line.friday || 0}`);
                sum += mo + tu + we + th + fr;
            });
        }
        this.totalHours = parseFloat(`${sum.toFixed(2)}`);
    }

    private correctDateTypes() {
        if (!this.report) return;
        this.report.start = this.report.start ? new Date(this.report.start) : undefined;
        this.report.end = this.report.end ? new Date(this.report.end) : undefined;
    }

    private async setInitialValues() {
        if (!this.report) this.report = {};

        const now = moment()
            .hour(0)
            .minute(0)
            .second(0);

        this.report.name = this.parent.name;
        this.report.street = this.parent.street;
        this.report.city = this.parent.city;
        this.report.zipCode = this.parent.zipCode;
        this.report.start = now.isoWeekday(1)
            .toDate();
        this.report.end = now.isoWeekday(5)
            .toDate();

        this.$forceUpdate();
    }

    private close() {
        this.$emit('close');
    }

    private onLineInput(line: DailyWageReportLine) {
        if (!this.report) return;

        this.report.lines = this.report.lines || [];
        this.report.lines = this.report.lines.map((l) => (l.id === line.id ? line : l));
        this.setTotalHours();
    }

    private async save(remote?: boolean, close?: boolean, callback?: () => void) {
        if (!this.report || !this.parent || !this.parent.id) return;

        this.submitting = true;
        this.generalError = null;
        this.localSaveError = false;
        this.remoteSaveError = false;
        this.showErrors = false;

        if (this.parentType === 'repair') {
            this.report.repairId = this.parent.id;
        } else if (this.parentType === 'project-group') {
            this.report.projectGroupId = this.parent.id;
        } else {
            return;
        }

        try {
            const result = remote ? await Service.reports.saveDailyWageReport(this.report) : await Service.reports.saveDailyWageReportLocal(this.report);
            this.handleResultErrors(result.remoteError, result.localError);

            this.submitting = false;
            this.emitUpdate(result.report);
            if (callback) {
                callback();
            } else if (close) {
                this.close();
            }
        } catch (e) {
            console.error(e);
            ErrorUtils.wrapError('Tagelohnzettel speichern', this.report, e)
                .then((wrapped) => this.generalError = wrapped);
        }
    }

    private handleResultErrors(remoteError?: any, localError?: any) {
        if (!localError) {
            this.lastSave = new Date();
        } else {
            console.error(localError);
            ErrorUtils.wrapError('Tagelohnzettel speichern', this.report, localError)
                .then((wrapped) => this.generalError = wrapped);
            this.localSaveError = true;
        }

        if (!remoteError) {
            this.lastSave = new Date();
        } else {
            console.error(remoteError);
            ErrorUtils.wrapError('Tagelohnzettel speichern', this.report, remoteError)
                .then((wrapped) => this.generalError = wrapped);
            this.remoteSaveError = true;
        }
    }

    private addLine(): void {
        if (!this.report) return;

        this.report.lines = this.report.lines || [];
        this.report.lines.push({
            id: this.getTempLineId(),
        });

        this.onReportChange(true);
        this.$forceUpdate();
    }

    private removeLine(id: number | null | undefined): void {
        if (!this.report) return;

        this.lineToRemove = null;
        if (!this.report.lines) return;

        this.report.lines = this.report.lines.filter((l) => l.id !== id);
        this.onReportChange(true);
        this.$forceUpdate();
    }

    private getTempLineId(): number {
        if (!this.report) return 0;

        let tempId = Math.floor(-999999999 * Math.random());
        // eslint-disable-next-line no-loop-func
        while (this.report.lines && this.report.lines.find((l) => l.id === tempId)) {
            tempId = Math.floor(-999999999 * Math.random());
        }

        return tempId;
    }

    private signReport(): void {
        this.save(false, false, () => {
            this.deleteDialogVisible = false;
            this.exportDialogVisible = true;
        });
    }

    private async downloadReport(): Promise<void> {
        if (!this.report) return;

        if (!this.uploader) {
            try {
                this.uploader = await Uploader.create();
            } catch (e) {
                console.error('failed to create uploader', e);
            }
        }

        if (!this.uploader || !this.isSigned) return;

        if (!this.report.fileId) {
            // Save remotely to generate document
            this.report = await Api.omb.saveWeeklyReport(this.report);
            // Save normally (remote + local) to persist file id change
            const result = await Service.reports.saveWeeklyReportLocal(this.report);
            if (result.remoteError || result.localError) {
                throw result.remoteError || result.localError;
            }
            this.report = result.report;
        }

        const name = `Tagelohnzettel_${this.report.name || '?'}_${this.report.number || '?'}`;
        try {
            await this.uploader.download(this.report.fileId || '', 'application/pdf', name);
        } catch (e) {
            ErrorUtils.wrapError('Tagelohnzettel PDF herunterladen', this.report, e)
                .then((wrapped) => this.generalError = wrapped);
        }
    }

    private showDeleteDialog(): void {
        this.exportDialogVisible = false;
        this.deleteDialogVisible = true;
    }

    private async deleteReport(): Promise<void> {
        if (!this.report) return;

        const toDelete = this.report;
        this.deleting = true;
        this.showErrors = false;

        Service.reports.deleteDailyWageReport(this.report)
            .then(() => {
                this.submitting = false;
                this.$emit('delete', toDelete.id);
                this.close();
            })
            .catch((e) => {
                console.error(e);
                this.deleting = false;
                ErrorUtils.wrapError('Tagelohnzettel löschen', this.report, e)
                    .then((wrapped) => this.generalError = wrapped);
                this.showErrors = true;
            });
    }

    private emitUpdate(toEmit?: DailyWageReport): void {
        this.report = toEmit || this.report;
        this.$emit('update', this.report);
    }

    private onExportSuccess(result: SaveReportResult) {
        if (!result.remoteError || !result.localError) this.emitUpdate(result.report);
        this.handleResultErrors(result.remoteError, result.localError);
    }

    private onReportChange(instantSave?: boolean) {
        if (!this.ready || this.isSigned || this.readonly) return;
        clearTimeout(this.autosaveTimeout);
        this.autosaveTimeout = setTimeout(this.save, instantSave ? 0 : 500);
    }

}
